
import { Button } from "@/components/ui/button";
import { Calendar, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";

interface CTASectionProps {
  title: string;
  description: string;
  primaryButtonText: string;
  secondaryButtonText?: string;
  primaryButtonAction?: () => void;
  primaryButtonLink?: string;
  secondaryButtonAction?: () => void;
  secondaryButtonLink?: string; // Added this prop for secondary button links
  className?: string;
}

export const CTASection = ({
  title,
  description,
  primaryButtonText,
  secondaryButtonText,
  primaryButtonAction,
  primaryButtonLink,
  secondaryButtonAction,
  secondaryButtonLink, // Added to props
  className = ''
}: CTASectionProps) => {
  const navigate = useNavigate();

  // Updated to navigate to book-appointment page instead of healthcoco
  const handlePrimaryAction = () => {
    if (primaryButtonAction) {
      primaryButtonAction();
    } else if (primaryButtonLink) {
      navigate(primaryButtonLink);
    } else {
      navigate("/dental-appointment-booking");
    }
  };

  // Default secondary action to navigate to contact page if not provided
  const handleSecondaryAction = () => {
    if (secondaryButtonAction) {
      secondaryButtonAction();
    } else if (secondaryButtonLink) {
      navigate(secondaryButtonLink);
    } else if (secondaryButtonText === "Contact Us") {
      navigate("/contact");
    }
  };

  // Render the button differently if a link is provided
  const renderPrimaryButton = () => {
    if (primaryButtonLink) {
      return (
        <Button className="btn-primary" asChild>
          <Link to={primaryButtonLink}>{primaryButtonText}</Link>
        </Button>
      );
    }
    
    return (
      <Button className="btn-primary" onClick={handlePrimaryAction}>
        {primaryButtonText}
      </Button>
    );
  };
  
  // Render the secondary button differently if a link is provided
  const renderSecondaryButton = () => {
    if (secondaryButtonLink) {
      return (
        <Button variant="outline" className="border-white bg-white text-dental-highlight hover:bg-white hover:text-dental-highlight" asChild>
          <Link to={secondaryButtonLink}>{secondaryButtonText}</Link>
        </Button>
      );
    }
    
    return (
      <Button 
        variant="outline" 
        className="border-white bg-white text-dental-highlight hover:bg-white hover:text-dental-highlight"
        onClick={handleSecondaryAction}
      >
        {secondaryButtonText}
      </Button>
    );
  };

  return (
    <section className={`relative py-12 sm:py-16 md:py-24 bg-dental-highlight text-white overflow-hidden ${className}`}>
      {/* Background Pattern - Subtle dots */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{ 
          backgroundImage: 'radial-gradient(rgba(255, 255, 255, 0.4) 1px, transparent 1px)', 
          backgroundSize: '20px 20px' 
        }}></div>
      </div>
      
      <div className="container-custom relative z-10">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{title}</h2>
          <p className="text-lg mb-8 text-white/90">{description}</p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {renderPrimaryButton()}
            
            {secondaryButtonText && renderSecondaryButton()}
          </div>
        </div>
      </div>
    </section>
  );
};
